import { createSelector } from 'reselect';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { CORE_ENTITY_SNAP_TARGET_TYPES, EntitySnapTargetType } from '../../library/layout-snapping/EntitySnapTarget';
import { RootState } from '../index';
import {
  getActiveBeamMode,
  getActiveCeilingMode,
  getActiveEnvelopeMode,
  getActiveFeaturePatternContainerMode,
  getActiveNonStructuralWallMode,
  getActiveRoofMode,
  getActiveWallMode,
  getActiveWorkLineMode,
} from './layoutVisualOptionsSelectors';
import { getPickingObjectInfo } from './pickingSelectors';

export const getCurrentLineSnappingContext = createSelector(
  (state: RootState) => {
    const info = getPickingObjectInfo(state);
    return info?.type === 'pickingLayoutLine' ? info.target : null;
  },

  (layoutLinePickingTarget) => {
    switch (layoutLinePickingTarget) {
      case 'roof-plane-definition':
      case 'ceiling-plane-definition':
      case 'feature-pattern-container-slope-base-edge':
        return layoutLinePickingTarget;

      case null:
        break;
    }

    // note that truss envelope creation is separate from "line picking" at the moment due to its complexity, so it does
    //   not have a snapping context and ignores this system entirely

    return 'general' as const;
  },
);

export type LinePickingSnappingTargetMode = EntitySnapTargetType[];

export const getEntitySnapTargetTypesForVisibleObjects = createSelector(
  getActiveRoofMode,
  getActiveCeilingMode,
  getActiveEnvelopeMode,
  getActiveWallMode,
  getActiveNonStructuralWallMode,
  getActiveBeamMode,
  getActiveWorkLineMode,
  getActiveFeaturePatternContainerMode,

  (
    roofMode,
    ceilingMode,
    envelopeMode,
    wallMode,
    nonStructuralWallMode,
    beamMode,
    workLineMode,
    featurePatternMode,
  ) => {
    const types: EntitySnapTargetType[] = [];

    if (roofMode !== 'none') types.push('roof');
    if (ceilingMode !== 'none') types.push('ceiling');
    if (envelopeMode !== 'none') types.push('truss-envelope');
    if (wallMode !== 'none') types.push('structural-wall');
    if (nonStructuralWallMode !== 'none') types.push('non-structural-wall');
    if (beamMode !== 'none') types.push('beam-bearing-envelope');
    if (workLineMode !== 'none') types.push('work-line');
    if (featurePatternMode !== 'none') types.push('feature-pattern-container-edge');

    types.push('uploaded-line'); // uploaded lines have no render mode toggle

    return types;
  },
);

export const getHiddenEntitySnapTargetTypesForVisibleObjects = createSelector(
  getEntitySnapTargetTypesForVisibleObjects,
  (visibleTypes): readonly EntitySnapTargetType[] =>
    CORE_ENTITY_SNAP_TARGET_TYPES.filter((type) => !visibleTypes.includes(type)),
);

export const getCurrentLineSnappingMode = createSelector(
  getCurrentLineSnappingContext,
  getEntitySnapTargetTypesForVisibleObjects,
  (context, visibleTypes): LinePickingSnappingTargetMode => {
    switch (context) {
      case 'roof-plane-definition':
      case 'ceiling-plane-definition':
      case 'general':
        return visibleTypes;

      case 'feature-pattern-container-slope-base-edge':
        return ['insertion-feature-pattern-container-edge'];

      default:
        assertExhaustiveSwitch(context);
    }
  },
);

export const getCurrentLineSnappingPreferredSnapType = createSelector(
  getCurrentLineSnappingContext,

  (context): EntitySnapTargetType | undefined => {
    switch (context) {
      case 'roof-plane-definition':
      case 'ceiling-plane-definition':
        // Planes can only have a relative geometry if they are based on a bearing envelope, so always snap to them
        //   when possible.
        return 'structural-wall';

      case 'feature-pattern-container-slope-base-edge':
        // this is the only target that's possible to select in this context, but it can't hurt to make it preferred too
        return 'insertion-feature-pattern-container-edge';

      case 'general':
        return undefined;

      default:
        assertExhaustiveSwitch(context);
    }
  },
);
